import { useCallback, useEffect, useState } from 'react';
import webApiAdmin from '@/lib/api-common/webApiAdmin';

// Poll interval while server is down to detect recovery (ms)
const RECOVERY_POLL_INTERVAL = 2 * 60_000; // 2 minutes
// Fetch timeout for the health check (ms)
const HEALTH_CHECK_TIMEOUT_MS = 8_000;

/**
 * Detects when the new admin BE (/api-v2) is down.
 *
 * Source of truth is `webApiAdmin`'s consecutive-5xx counter (see
 * `CONSECUTIVE_SERVER_ERROR_THRESHOLD` in `webApiAdmin.ts`): the banner shows once 10
 * consecutive requests across the whole app come back 5xx, and the streak resets to 0
 * the moment any request succeeds (200/201) — this is a pure streak, not a
 * time-windowed count, so scattered failures mixed with successes never trip it.
 *
 * While down, polls a lightweight existing endpoint
 * (/api-v2/admin/notifications/load — tiny GET) every RECOVERY_POLL_INTERVAL and, on a
 * non-5xx response, explicitly resets the shared counter so the banner clears even if
 * the admin isn't triggering any other requests. NOTE: the old /admin/health path does
 * not exist on the v2 BE (always 404 → always < 500 → falsely "recovered"); a real
 * endpoint returns 5xx while the BE is genuinely down.
 */
export function useApiDownDetection() {
  const [isDown, setIsDown] = useState(false);

  useEffect(() => webApiAdmin.subscribeDownState(setIsDown), []);

  const checkRecovery = useCallback(async () => {
    try {
      const res = await fetch(`/api-v2/admin/notifications/load?_=${Date.now()}`, {
        cache: 'no-store',
        credentials: 'include',
        signal: AbortSignal.timeout(HEALTH_CHECK_TIMEOUT_MS),
      });
      if (res.status < 500) {
        webApiAdmin.resetConsecutiveServerErrors();
      }
    } catch {
      // still unreachable — keep banner
    }
  }, []);

  useEffect(() => {
    if (!isDown) return;
    const timer = setInterval(() => void checkRecovery(), RECOVERY_POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [isDown, checkRecovery]);

  return { isDown };
}
